// Navbar.jsx
import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Stack,
  Box,
  Avatar,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Tabs,
  Tab,
  ClickAwayListener,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  SignedIn,
  SignedOut,
  UserButton,
  SignIn,
  SignUp,
  useAuth,
  useUser,
} from '@clerk/clerk-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import PersonIcon from '@mui/icons-material/Person';
import AppsRoundedIcon from '@mui/icons-material/AppsRounded';
import AddBoxRoundedIcon from '@mui/icons-material/AddBoxRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import VideocamIcon from '@mui/icons-material/Videocam';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import PhotoRoundedIcon from '@mui/icons-material/PhotoRounded';
import PhotoLibraryRoundedIcon from '@mui/icons-material/PhotoLibraryRounded';
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import CookieOutlinedIcon from '@mui/icons-material/CookieOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';

import logo from '../../assets/logo.png';

import SearchBar from '../SearchBar/SearchBar';
import QubiqRepublicButton from '../QubiqRepublicButton/QubiqRepublicButton';
import BannerCounter from '../BannerCounter/BannerCounter';
import LiveUserRankBadge from '../Badge/LiveUserRankBadge';
import { LEGAL_LINKS } from '../../utils/legalConfig';
import { pressable3dHandlers } from '../../utils/pressable3d';
import {
  formatNearbyCountLabel,
  getStoredMapExplorationLocation,
  storeMapExplorationLocation,
} from '../../utils/mapExploration';
import {
  clearPendingCapturedExploreFile,
  clearPendingCapturedMomentFile,
  setPendingCapturedExploreFile,
  setPendingCapturedMomentFile,
} from '../../utils/mobileStudioCapture';
import useDisableMobileTextAutofocus from '../../hooks/useDisableMobileTextAutofocus';
import LanguageDialog from '../Language/LanguageDialog';
import { normalizeToSupportedLanguage } from '../../i18n/config';
import { getApiBase } from '../../utils/apiBase';
import { syncClerkProfileToBackend } from '../../utils/clerkProfileSync';
import { qubAmountToAtomicBigInt } from '../../utils/qubAmount';
import useMessagesUnreadCount from '../../hooks/useMessagesUnreadCount';
import { openInviteFriendsPrompt } from '../../utils/inviteFriends';

const PUBLIC_API_BASE = getApiBase();

const legalMenuIcons = {
  description: <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />,
  gavel: <GavelRoundedIcon sx={{ fontSize: 18 }} />,
  shield: <ShieldOutlinedIcon sx={{ fontSize: 18 }} />,
  cookie: <CookieOutlinedIcon sx={{ fontSize: 18 }} />,
  policy: <PolicyOutlinedIcon sx={{ fontSize: 18 }} />,
};

const CREATOR_ACTION_EVENT = 'qubiq-creator-action';
const CREATOR_STATUS_CHANGED_EVENT = 'qubiq-creator-status-changed';
const MOBILE_EXPLORE_PLAYER_VISIBILITY_EVENT =
  'qubiq-mobile-explore-player-visibility';

const ContactCreatorIcon = ({ size = 20 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    <EmailRoundedIcon sx={{ fontSize: size, color: 'inherit' }} />
  </Box>
);

const Navbar = () => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const hideNavbarBadge = useMediaQuery('(max-width: 350px)');
  const hideNavbarLevel = useMediaQuery('(max-width: 332px)');
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const location = useLocation();                 
  const isHome = location.pathname === '/';
  const isSearchRoute = location.pathname.startsWith('/search');
  const isMapRoute = location.pathname.startsWith('/map');
  const isStudioRoute = location.pathname.startsWith('/studio');

  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState('signup');
  const [guestProfileMenuOpen, setGuestProfileMenuOpen] = useState(false);
  const [languageDialogOpen, setLanguageDialogOpen] = useState(false);
  const [creatorWelcomeOpen, setCreatorWelcomeOpen] = useState(false);
  const [creatorContactOpen, setCreatorContactOpen] = useState(false);
  const [creatorActionLoading, setCreatorActionLoading] = useState(false);
  const [pendingCreatorSignup, setPendingCreatorSignup] = useState(false);
  const [pendingStudioOpen, setPendingStudioOpen] = useState(false);
  const [pendingStudioPath, setPendingStudioPath] = useState('/studio');
  const [pendingMomentOpen, setPendingMomentOpen] = useState(false);
  const [isMobileExplorePlayerOpen, setIsMobileExplorePlayerOpen] = useState(false);
  const [guestBannerCounterValue, setGuestBannerCounterValue] = useState(null);
  const [mobileCreateMenuOpen, setMobileCreateMenuOpen] = useState(false);
  const [mobileExploreSourceOpen, setMobileExploreSourceOpen] = useState(false);
  const [mobileMomentSourceOpen, setMobileMomentSourceOpen] = useState(false);
  const [mobileMomentCameraDialogOpen, setMobileMomentCameraDialogOpen] = useState(false);
  const [mobileMomentCameraLoading, setMobileMomentCameraLoading] = useState(false);
  const [mobileMomentCameraError, setMobileMomentCameraError] = useState('');
  const [pendingExploreMode, setPendingExploreMode] = useState('');
  const mobileExploreLibraryInputRef = useRef(null);
  const mobileMomentLibraryInputRef = useRef(null);
  const mobileMomentCameraInputRef = useRef(null);
  const mobileMomentCameraVideoRef = useRef(null);
  const mobileMomentCameraCanvasRef = useRef(null);
  const mobileMomentCameraStreamRef = useRef(null);

  const mobileSourceOptionButtonSx = {
    justifyContent: 'flex-start',
    borderRadius: '5px',
    px: 1.6,
    py: 1.25,
    textTransform: 'none',
    fontWeight: 800,
    color: '#fff',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&:active': {
      backgroundColor: 'transparent',
    },
    '&.Mui-focusVisible': {
      backgroundColor: 'transparent',
    },
  };

  useDisableMobileTextAutofocus(authOpen);

  const { isSignedIn, getToken } = useAuth();

  const [clientId, setClientId] = useState("");

  useEffect(() => {
    const id =
      typeof window !== "undefined"
        ? window.localStorage.getItem("qubiq_client_id") || ""
        : "";
    setClientId(id);

    // Optionnel: si ton app peut changer ce clientId pendant la session
    const onStorage = (e) => {
      if (e.key === "qubiq_client_id") setClientId(e.newValue || "");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleVisibility = (event) => {
      setIsMobileExplorePlayerOpen(Boolean(event?.detail?.open));
    };

    window.addEventListener(
      MOBILE_EXPLORE_PLAYER_VISIBILITY_EVENT,
      handleVisibility
    );

    return () => {
      window.removeEventListener(
        MOBILE_EXPLORE_PLAYER_VISIBILITY_EVENT,
        handleVisibility
      );
    };
  }, []);


  const { user } = useUser();
  const desktopProfileImageInputRef = useRef(null);
  const [desktopAvatarUploading, setDesktopAvatarUploading] = useState(false);
  const [desktopAvatarError, setDesktopAvatarError] = useState('');
  const [clerkPopoverAvatarHost, setClerkPopoverAvatarHost] = useState(null);
  const userProfilePath = user?.id ? `/profile/${encodeURIComponent(user.id)}` : '/';
  const [notificationCount, setNotificationCount] = useState(0);
  const [mapNearbyCount, setMapNearbyCount] = useState(0);
  const { messageCount, hasUnreadMessages } = useMessagesUnreadCount();
  const hasNotifications = notificationCount > 0;
  const hasMapNearby = mapNearbyCount > 0;
  const profileBadgeCount = notificationCount + messageCount;
  const hasProfileBadge = profileBadgeCount > 0;

  useEffect(() => {
    let cancelled = false;

    const loadNotificationCount = async () => {
      if (!isSignedIn) {
        if (!cancelled) setNotificationCount(0);
        return;
      }

      try {
        const token = await getToken();
        if (!token) {
          if (!cancelled) setNotificationCount(0);
          return;
        }

        const response = await fetch(`${PUBLIC_API_BASE}/notifications/count`, {
          credentials: 'include',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data?.error || 'notifications_count_failed');
        }

        if (!cancelled) {
          setNotificationCount(
            Math.max(0, Number(data?.count ?? data?.unseenCount ?? 0))
          );
        }
      } catch {
        if (!cancelled) setNotificationCount(0);
      }
    };

    loadNotificationCount();
    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'hidden') return;
      loadNotificationCount();
    };
    const intervalId = window.setInterval(loadNotificationCount, 30000);
    window.addEventListener('qubiq-notifications-refresh', loadNotificationCount);
    window.addEventListener('focus', handleVisibilityOrFocus);
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('qubiq-notifications-refresh', loadNotificationCount);
      window.removeEventListener('focus', handleVisibilityOrFocus);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
    };
  }, [isSignedIn, getToken, location.pathname, location.search]);

  const [isCreator, setIsCreator] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchNearbyMapCount = async (coordinates) => {
      if (!coordinates?.latitude || !coordinates?.longitude) {
        if (!cancelled) setMapNearbyCount(0);
        return;
      }

      try {
        const token = isSignedIn ? await getToken().catch(() => null) : null;
        const response = await fetch(
          `${PUBLIC_API_BASE}/map-exploration/nearby-count?latitude=${encodeURIComponent(
            coordinates.latitude
          )}&longitude=${encodeURIComponent(coordinates.longitude)}`,
          {
            credentials: 'include',
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          }
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data?.error || 'nearby_count_failed');
        }

        if (!cancelled) {
          setMapNearbyCount(Math.max(0, Number(data?.count || 0)));
        }
      } catch {
        if (!cancelled) setMapNearbyCount(0);
      }
    };

    const refreshStoredOrGrantedLocation = async () => {
      const stored = getStoredMapExplorationLocation();
      if (stored) {
        await fetchNearbyMapCount(stored);
      }

      if (!navigator?.geolocation || !navigator?.permissions?.query) {
        return;
      }

      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        if (permission.state !== 'granted') return;

        navigator.geolocation.getCurrentPosition(
          (position) => {
            const nextLocation = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              updatedAt: new Date().toISOString(),
            };
            storeMapExplorationLocation(nextLocation);
            fetchNearbyMapCount(nextLocation);
          },
          () => {},
          {
            enableHighAccuracy: true,
            timeout: 8000,
            maximumAge: 120000,
          }
        );
      } catch {}
    };

    refreshStoredOrGrantedLocation();
    const intervalId = window.setInterval(refreshStoredOrGrantedLocation, 60000);
    window.addEventListener('focus', refreshStoredOrGrantedLocation);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refreshStoredOrGrantedLocation);
    };
  }, [isSignedIn, getToken]);

  useEffect(() => {
    const email = user?.primaryEmailAddress?.emailAddress?.toLowerCase?.() || "";

    if (!isSignedIn || !email) {
      setIsCreator(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`/api/creators/is-creator?email=${encodeURIComponent(email)}`);
        const data = await res.json();
        if (!cancelled) setIsCreator(!!data?.isCreator);
      } catch {
        if (!cancelled) setIsCreator(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isSignedIn, user]);

  useEffect(() => {
    const handleCreatorAction = (event) => {
      const action = event?.detail?.action;
      if (action === 'create') {
        if (!isSignedIn) {
          setPendingCreatorSignup(true);
          openAuth('signup');
          return;
        }
        createCreatorAccount();
        return;
      }

      if (action === 'contact') {
        setCreatorContactOpen(true);
      }
    };

    const handleCreatorStatusChanged = (event) => {
      if (typeof event?.detail?.isCreator === 'boolean') {
        setIsCreator(event.detail.isCreator);
      }
    };

    window.addEventListener(CREATOR_ACTION_EVENT, handleCreatorAction);
    window.addEventListener(CREATOR_STATUS_CHANGED_EVENT, handleCreatorStatusChanged);
    return () => {
      window.removeEventListener(CREATOR_ACTION_EVENT, handleCreatorAction);
      window.removeEventListener(CREATOR_STATUS_CHANGED_EVENT, handleCreatorStatusChanged);
    };
  }, [isSignedIn, creatorActionLoading]);


  const openAuth = (mode) => {
    setGuestProfileMenuOpen(false);
    setAuthTab(mode);
    setAuthOpen(true);
  };

  const closeAuth = () => {
    setAuthOpen(false);
    setMobileCreateMenuOpen(false);
    setMobileMomentSourceOpen(false);
    setMobileMomentCameraDialogOpen(false);
    if (!isSignedIn) {
      setPendingCreatorSignup(false);
      setPendingStudioOpen(false);
      setPendingStudioPath('/studio');
      setPendingMomentOpen(false);
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleOpenAuthDialog = (event) => {
      const requestedTab = String(event?.detail?.tab || '').trim().toLowerCase();
      openAuth(requestedTab === 'signin' ? 'signin' : 'signup');
    };

    window.addEventListener('qubiq:open-auth-dialog', handleOpenAuthDialog);

    return () => {
      window.removeEventListener('qubiq:open-auth-dialog', handleOpenAuthDialog);
    };
  }, [isSignedIn]);

  const emitCreatorStatusChanged = (nextIsCreator) => {
    window.dispatchEvent(
      new CustomEvent(CREATOR_STATUS_CHANGED_EVENT, {
        detail: { isCreator: nextIsCreator },
      })
    );
  };

  const createCreatorAccount = async ({
    navigateToStudio = false,
    showWelcome = true,
    navigateToStudioPath = '/studio',
  } = {}) => {
    if (creatorActionLoading) return false;
    if (!isSignedIn) {
      setPendingCreatorSignup(true);
      openAuth('signin');
      return false;
    }

    try {
      setCreatorActionLoading(true);
      const token = await getToken();
      const response = await fetch(`${PUBLIC_API_BASE}/creators/create-account`, {
        method: 'POST',
        credentials: 'include',
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : undefined,
      });
      const data = await response.json().catch(() => ({}));

      if (response.status === 401) {
        setPendingCreatorSignup(true);
        openAuth('signin');
        return false;
      }

      if (!response.ok) {
        throw new Error(data?.error || 'creator_account_create_failed');
      }

      setIsCreator(true);
      emitCreatorStatusChanged(true);
      if (showWelcome) {
        setCreatorWelcomeOpen(true);
      }
      setCreatorContactOpen(false);
      setPendingCreatorSignup(false);
      setPendingStudioOpen(false);
      setPendingStudioPath('/studio');
      if (navigateToStudio) {
        navigate(navigateToStudioPath);
      }
      return true;
    } catch (error) {
      console.error('createCreatorAccount error:', error);
      return false;
    } finally {
      setCreatorActionLoading(false);
    }
  };

  const handleCreateOnQubiqClick = () => {
    if (isCreator) {
      setCreatorContactOpen(true);
      return;
    }

    if (!isSignedIn) {
      setPendingCreatorSignup(true);
      openAuth('signin');
      return;
    }

    createCreatorAccount();
  };

  const ensureMobileExploreCreationAccess = async (mode = 'camera') => {
    const captureExplorePath = '/studio/capture-explore';
    setMobileCreateMenuOpen(false);
    setMobileMomentSourceOpen(false);
    setMobileExploreSourceOpen(false);
    clearPendingCapturedMomentFile();

    if (!isSignedIn) {
      setPendingCreatorSignup(true);
      setPendingStudioOpen(mode === 'camera');
      setPendingStudioPath(mode === 'camera' ? captureExplorePath : '/studio');
      openAuth('signin');
      return false;
    }

    let canOpenStudio = isCreator;
    if (!canOpenStudio) {
      canOpenStudio = await createCreatorAccount({
        navigateToStudio: false,
        showWelcome: false,
      });
    }

    if (!canOpenStudio) {
      return false;
    }

    return true;
  };

  const handleMobileCreateVideoClick = () => {
    setMobileCreateMenuOpen(false);
    setMobileMomentSourceOpen(false);
    setMobileExploreSourceOpen(true);
    setPendingExploreMode('');
  };

  const handleMobileCreateMenuOpen = useCallback(() => {
    setMobileExploreSourceOpen(false);
    setMobileMomentSourceOpen(false);
    setMobileMomentCameraDialogOpen(false);
    setPendingExploreMode('');
    setMobileCreateMenuOpen(true);
  }, []);

  const handleMobileExploreCameraClick = useCallback(async () => {
    const captureExplorePath = '/studio/capture-explore';
    setPendingExploreMode('camera');
    clearPendingCapturedExploreFile();

    const canOpenStudio = await ensureMobileExploreCreationAccess('camera');
    if (!canOpenStudio) {
      clearPendingCapturedExploreFile();
      return;
    }

    navigate(captureExplorePath);
  }, [isSignedIn, isCreator, navigate]);

  const handleMobileExploreLibraryClick = async () => {
    setPendingExploreMode('gallery');
    clearPendingCapturedExploreFile();

    const canOpenStudio = await ensureMobileExploreCreationAccess('gallery');
    if (!canOpenStudio) {
      clearPendingCapturedExploreFile();
      return;
    }

    mobileExploreLibraryInputRef.current?.click?.();
  };

  const handleMobileExploreFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = '';
    if (!(file instanceof File)) return;

    setMobileExploreSourceOpen(false);
    clearPendingCapturedMomentFile();
    clearPendingCapturedExploreFile();
    setPendingCapturedExploreFile(file, {
      captureSource: 'mobile_gallery_picker',
      source: 'mobile_navbar_gallery',
      mimeType: file.type || '',
    });
    navigate(`/studio?tab=explore&exploreSection=explores&pendingCapture=${Date.now()}`);
  };

  const openMobileMomentComposer = useCallback(() => {
    setMobileCreateMenuOpen(false);
    setMobileExploreSourceOpen(false);
    setMobileMomentSourceOpen(false);
    setMobileMomentCameraDialogOpen(false);
    navigate('/map?mode=personal&createMoment=1');
  }, [navigate]);

  const handleMobileMomentCreateClick = () => {
    if (!isSignedIn) {
      setPendingMomentOpen(true);
      setMobileCreateMenuOpen(false);
      setMobileMomentSourceOpen(false);
      openAuth('signin');
      return;
    }

    setMobileCreateMenuOpen(false);
    setMobileMomentSourceOpen(true);
  };

  const handleMobileMomentFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = '';
    if (!(file instanceof File)) return;

    setMobileMomentSourceOpen(false);
    clearPendingCapturedExploreFile();
    clearPendingCapturedMomentFile();
    setPendingCapturedMomentFile(file, {
      source: 'mobile_navbar',
    });
    openMobileMomentComposer();
  };

  const stopMobileMomentCameraStream = () => {
    const stream = mobileMomentCameraStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      mobileMomentCameraStreamRef.current = null;
    }
  };

  const handleMobileMomentCameraOpen = () => {
    if (isSmDown) {
      mobileMomentCameraInputRef.current?.click?.();
      return;
    }
    setMobileMomentSourceOpen(false);
    setMobileMomentCameraError('');
    setMobileMomentCameraDialogOpen(true);
  };

  const handleDesktopMomentCameraCapture = async () => {
    const video = mobileMomentCameraVideoRef.current;
    const canvas = mobileMomentCameraCanvasRef.current;
    if (!video || !canvas) return;

    const width = Math.max(1, video.videoWidth || 1280);
    const height = Math.max(1, video.videoHeight || 720);
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      setMobileMomentCameraError("Impossible d'accéder à la capture photo.");
      return;
    }

    context.drawImage(video, 0, 0, width, height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
    if (!(blob instanceof Blob)) {
      setMobileMomentCameraError("Impossible de capturer la photo.");
      return;
    }

    clearPendingCapturedExploreFile();
    clearPendingCapturedMomentFile();
    setPendingCapturedMomentFile(
      new File([blob], `moment-${Date.now()}.jpg`, { type: 'image/jpeg' }),
      { source: 'desktop_webcam' }
    );
    openMobileMomentComposer();
  };

  const handleContactCreatorClick = () => {
    setCreatorContactOpen(true);
  };

  useEffect(() => {
    if (!authOpen) return;

    // Informe les pages (VideoDetail, feed, etc.) que la modale d'auth s'ouvre
    window.dispatchEvent(new Event('qubiq-auth-modal-open'));
  }, [authOpen]);


  const handleTabChange = (_event, value) => setAuthTab(value);
  const [isSearchbarOpen, setIsSearchbarOpen] = useState(false);

  const profileColorSignedOut = authOpen ? '#ffdb42' : '#fff';
  const isTransparentMobileOverlayNavbar =
    isSmDown && (isMobileExplorePlayerOpen || isMapRoute);
  const shouldShowMobileCreateAccountButton =
    isSmDown &&
    !isSignedIn &&
    !isTransparentMobileOverlayNavbar &&
    guestBannerCounterValue != null &&
    qubAmountToAtomicBigInt(guestBannerCounterValue || '0') === 0n;



  // ✅ Quand l'utilisateur vient de se connecter / inscrire, on ferme la modale
  useEffect(() => {
    if (isSignedIn && authOpen) {
      setAuthOpen(false);
    }
  }, [isSignedIn, authOpen]);

  useEffect(() => {
    if (!isSignedIn || !pendingCreatorSignup || isCreator) return;
    createCreatorAccount({
      navigateToStudio: pendingStudioOpen,
      navigateToStudioPath: pendingStudioPath,
      showWelcome: !pendingStudioOpen,
    });
  }, [isSignedIn, pendingCreatorSignup, isCreator, pendingStudioOpen, pendingStudioPath]);

  useEffect(() => {
    if (!isSignedIn || !pendingMomentOpen) return;
    setPendingMomentOpen(false);
    openMobileMomentComposer();
  }, [isSignedIn, pendingMomentOpen]);

  useEffect(() => {
    if (!isSignedIn || !pendingExploreMode) return;
    if (pendingExploreMode === 'camera') {
      setPendingExploreMode('');
      handleMobileExploreCameraClick();
      return;
    }

    setPendingExploreMode('');
    setMobileExploreSourceOpen(true);
  }, [handleMobileExploreCameraClick, isSignedIn, pendingExploreMode]);

  useEffect(() => {
    if (!isTransparentMobileOverlayNavbar) return;
    setIsSearchbarOpen(false);
  }, [isTransparentMobileOverlayNavbar]);

  useEffect(() => {
    if (!mobileMomentCameraDialogOpen) {
      stopMobileMomentCameraStream();
      setMobileMomentCameraLoading(false);
      setMobileMomentCameraError('');
      return undefined;
    }

    let cancelled = false;

    const startCamera = async () => {
      if (!navigator?.mediaDevices?.getUserMedia) {
        setMobileMomentCameraLoading(false);
        setMobileMomentCameraError("La webcam n'est pas disponible sur cet appareil.");
        return;
      }

      try {
        setMobileMomentCameraLoading(true);
        setMobileMomentCameraError('');
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'user',
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        mobileMomentCameraStreamRef.current = stream;
        if (mobileMomentCameraVideoRef.current) {
          mobileMomentCameraVideoRef.current.srcObject = stream;
          await mobileMomentCameraVideoRef.current.play().catch(() => undefined);
        }
      } catch {
        setMobileMomentCameraError("Impossible d'ouvrir la webcam.");
      } finally {
        if (!cancelled) {
          setMobileMomentCameraLoading(false);
        }
      }
    };

    startCamera();

    return () => {
      cancelled = true;
      stopMobileMomentCameraStream();
    };
  }, [mobileMomentCameraDialogOpen]);

  useEffect(() => {
    if (isSmDown || typeof document === 'undefined') return undefined;

    const body = document.body;
    const documentElement = document.documentElement;
    const previousBodyOverflow = body.style.overflow;
    const previousDocumentOverflow = documentElement.style.overflow;

    const syncPopoverScrollLock = () => {
      const popover = document.querySelector('[data-clerk-popover]');
      const hasClerkPopover = Boolean(popover);
      if (hasClerkPopover) {
        body.style.overflow = 'hidden';
        documentElement.style.overflow = 'hidden';
      } else {
        body.style.overflow = previousBodyOverflow;
        documentElement.style.overflow = previousDocumentOverflow;
      }

      let nextHost = null;
      if (popover) {
        const card =
          popover.querySelector('.cl-userButtonPopoverCard') ||
          popover.querySelector('.cl-userButtonPopoverMain') ||
          popover;
        if (card) {
          let host = popover.querySelector('[data-qubiq-clerk-avatar-editor]');
          if (!host) {
            host = document.createElement('div');
            host.setAttribute('data-qubiq-clerk-avatar-editor', 'true');
            card.prepend(host);
          }
          nextHost = host;
        }
      }
      setClerkPopoverAvatarHost((current) => (current === nextHost ? current : nextHost));
    };

    syncPopoverScrollLock();

    const observer = new MutationObserver(() => {
      syncPopoverScrollLock();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      body.style.overflow = previousBodyOverflow;
      documentElement.style.overflow = previousDocumentOverflow;
      setClerkPopoverAvatarHost(null);
    };
  }, [isSmDown]);

  const handleDesktopPopoverAvatarFileChange = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file || !user) return;

      setDesktopAvatarUploading(true);
      setDesktopAvatarError('');

      try {
        await user.setProfileImage({ file });
        await user.reload?.();
        await syncClerkProfileToBackend({ getToken });
      } catch (error) {
        setDesktopAvatarError(
          error?.errors?.[0]?.longMessage ||
            error?.errors?.[0]?.message ||
            error?.message ||
            "Impossible de mettre à jour la photo de profil."
        );
      } finally {
        setDesktopAvatarUploading(false);
      }
    },
    [getToken, user]
  );

  // ✅ URL courante pour forcer Clerk à rester sur la page actuelle
  const currentUrl =
    typeof window !== 'undefined'
      ? window.location.pathname + window.location.search
      : '/';

  const shouldShowRankBadge = !hideNavbarBadge;
  const shouldShowRankLevel = !hideNavbarLevel;
  const mobileNavbarRightClusterMarginRight = -1;
  const mobileMapOverlayRightClusterMarginRight = 4.4;

  const clerkAppearance = {
    elements: {
      rootBox: {
        width: "100%",
        maxWidth: "100%",
        transform: "translateX(5px)",
      },
      card: {
        width: "100%",
        maxWidth: "360px",
        boxShadow: "none",
        borderRadius: "5px",
        margin: "0 auto",
        transform: isSmDown ? "translateX(0px)" : "none",
        boxSizing: "border-box",
        padding: "16px",
        maxHeight: "none",
        overflowY: "visible",
      },
      userButtonPopoverCard: {
        maxHeight: "min(80vh, 720px)",
        overflowY: "auto",
        borderRadius: "5px",
      },
      userButtonPopoverMain: {
        maxHeight: "min(80vh, 720px)",
        overflowY: "auto",
        borderRadius: "5px",
      },
      headerTitle: {
        fontSize: "18px",
      },
      headerSubtitle: {
        fontSize: "13px",
        whiteSpace: "normal",
      },
      form: {
        gap: "5px",
      },
      formField: {
        marginBottom: "8px",
      },
      formButtonPrimary: {
        padding: "8px 12px",
        fontSize: "15px",
        borderRadius: "5px",
        backgroundColor: "#ffcf00",
        color: "#121216",
        boxShadow: "0px 3px 0 #d38200",
        "& svg": {
          display: "none",
        },
      },
      formFieldInput: {
        borderRadius: "5px",
      },
      formFieldInput__identifier: {
        borderRadius: "5px",
      },
      formFieldInput__password: {
        borderRadius: "5px",
      },
      formFieldInput__emailAddress: {
        borderRadius: "5px",
      },
      formFieldInput__username: {
        borderRadius: "5px",
      },
      formFieldInput__firstName: {
        borderRadius: "5px",
      },
      formFieldInput__lastName: {
        borderRadius: "5px",
      },
      otpCodeFieldInput: {
        borderRadius: "5px",
      },
      formFieldRow: {
        borderRadius: "5px",
      },
      formResendCodeLink: {
        borderRadius: "5px",
      },
      formSecondaryAction: {
        borderRadius: "5px",
      },
      formFieldInputShowPasswordButton: {
        borderRadius: "5px",
      },
      socialButtonsBlockButton: {
        borderRadius: "5px",
      },
      socialButtonsBlockButtonArrow: {
        borderRadius: "5px",
      },
      socialButtonsProviderIcon__github: {
        borderRadius: "5px",
      },
      socialButtonsProviderIcon__google: {
        borderRadius: "5px",
      },
      userButtonPopoverActionButton: {
        fontSize: "1.1rem !important",
        border: "none",
        borderBottom: "none",
        boxShadow: "none",
      },
      userButtonPopoverActionButtonText: {
        fontSize: "1.1rem !important",
      },
      userButtonPopoverActionButtonIcon: {
        width: "18px",
        height: "18px",
      },
      userButtonPopoverActionButton__manageAccount: {
        border: "none",
        borderBottom: "none",
        boxShadow: "none",
      },
      menuItem: {
        border: "none",
        borderBottom: "none",
        boxShadow: "none",
      },
      menuList: {
        border: "none",
        boxShadow: "none",
      },
      footer: { display: "none" },
      logoImage: { display: "none" },
    },
  };

  const ICON_SLOT_H = 26;

  const compactMessageLabel = messageCount > 9 ? '9+' : messageCount;
  const compactMapNearbyLabel = formatNearbyCountLabel(mapNearbyCount);
  const activeLanguage = normalizeToSupportedLanguage(
    i18n.resolvedLanguage || i18n.language
  );
  const languageMenuLabel = `${t("profileMenu.language")} · ${t(
    `languages.${activeLanguage}`
  )}`;

  const renderCountBadge = (
    count,
    size = '18px',
    fontSize = '0.65rem',
    options = {}
  ) =>
    count > 0 ? (
      <Box
        sx={{
          position: 'absolute',
          top: options.top ?? -4,
          right: options.right ?? -4,
          minWidth: size,
          height: size,
          px: options.px ?? 0.45,
          borderRadius: options.borderRadius ?? '999px',
          bgcolor: '#ff4747',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 900,
          fontSize,
          lineHeight: 1,
          border: '2px solid #26262c',
          pointerEvents: 'none',
          boxShadow: '0 4px 10px rgba(0,0,0,0.22)',
        }}
      >
        {count > 99 ? '99+' : count}
      </Box>
    ) : null;

  const renderNotificationBadge = (
    size = '18px',
    fontSize = '0.65rem',
    options = {}
  ) =>
    hasNotifications
      ? renderCountBadge(notificationCount, size, fontSize, options)
      : null;

  const renderMapBadge = (
    size = '18px',
    fontSize = '0.65rem',
    options = {}
  ) =>
    hasMapNearby ? (
      <Box
        sx={{
          position: 'absolute',
          top: options.top ?? -4,
          right: options.right ?? -4,
          minWidth: size,
          height: size,
          px: options.px ?? 0.45,
          borderRadius: options.borderRadius ?? '999px',
          bgcolor: '#ff4747',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 900,
          fontSize,
          lineHeight: 1,
          border: '2px solid #26262c',
          pointerEvents: 'none',
          boxShadow: '0 4px 10px rgba(0,0,0,0.22)',
        }}
      >
        {compactMapNearbyLabel}
      </Box>
    ) : null;

  const notificationMenuIcon = (
      <Box sx={{ position: 'relative', width: 'var(--clerk-user-menu-icon-size)', height: 'var(--clerk-user-menu-icon-size)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <NotificationsRoundedIcon sx={{ fontSize: 'var(--clerk-user-menu-icon-size)' }} />
        {renderNotificationBadge('16px', '0.58rem')}
      </Box>
  );

  const messagesMenuIcon = (
    <Box sx={{ position: 'relative', width: 'var(--clerk-user-menu-icon-size)', height: 'var(--clerk-user-menu-icon-size)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <ForumRoundedIcon sx={{ fontSize: 'var(--clerk-user-menu-icon-size)' }} />
      {hasUnreadMessages ? renderCountBadge(messageCount, '16px', '0.58rem') : null}
    </Box>
  );

  const legalMenuItems = LEGAL_LINKS.map((item) => (
    <UserButton.Link
      key={item.key}
      href={item.key === 'cookies' ? `${item.path}?manage=1` : item.path}
      label={
        item.key === 'cookies'
          ? t("profileMenu.cookiesAndConsent")
          : t(`legalLinks.${item.key}`)
      }
      labelIcon={legalMenuIcons[item.icon]}
    />
  ));

  const creatorMenuActionLabel = isCreator
    ? t("profileMenu.contactUs")
    : t("profileMenu.createOnQubiq");
  const creatorMenuActionIcon = isCreator ? (
    <ContactCreatorIcon size={18} />
  ) : (
    <VideocamIcon sx={{ fontSize: 'var(--clerk-user-menu-icon-size)' }} />
  );

  return (
    <>
      {/* NAVBAR TOP */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent={isTransparentMobileOverlayNavbar ? 'flex-end' : 'space-between'}
        p={0.5}
        pr={isTransparentMobileOverlayNavbar ? 2 : 2}
        pl={isTransparentMobileOverlayNavbar ? 2 : 2}
        height={53}
        boxShadow="0 0 2px rgba(255, 219, 66, 0.2), 0 0 5px rgba(115, 115, 115, 0.1)"
        position={isTransparentMobileOverlayNavbar ? 'fixed' : 'sticky'}
        top={0}
        left={isTransparentMobileOverlayNavbar ? 0 : undefined}
        right={isTransparentMobileOverlayNavbar ? 0 : undefined}
        width={isTransparentMobileOverlayNavbar ? '100%' : undefined}
        zIndex={30}
        className={`Navbar-header${
          isTransparentMobileOverlayNavbar ? ' Navbar-header--explore-mobile' : ''
        }`}
        sx={{
          gap: isTransparentMobileOverlayNavbar ? 0.5 : 1,
          bgcolor: isTransparentMobileOverlayNavbar
            ? 'transparent !important'
            : 'background.paper',
          boxShadow: isTransparentMobileOverlayNavbar
            ? 'none !important'
            : '0 0 2px rgba(255, 219, 66, 0.2), 0 0 5px rgba(115, 115, 115, 0.1)',
          backgroundImage: 'none',
          pointerEvents: isTransparentMobileOverlayNavbar ? 'none' : 'auto',
        }}
      >
        {/* Logo */}
        {!isTransparentMobileOverlayNavbar && (
          <Link
            to="/"
            state={{ resetFeed: true }}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Box
              component="img"
              src={logo}
              alt="logo"
              loading="eager"
              fetchPriority="high"
              decoding="sync"
              sx={{ height: { xs: 35, sm: 39, md: 43 } }}
            />
          </Link>
        )}

        {/* SearchBar : visible seulement ≥ sm */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            px: { xs: 1, md: 2 },
            display: { xs: 'none', sm: 'block' }, // 👈 masqué sur smartphone
          }}
        >
          <SearchBar />
        </Box>

        {/* Right zone */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={isSmDown ? 0.45 : 1}
          sx={{
            flexShrink: 0,
            minWidth: 0,
            maxWidth: isTransparentMobileOverlayNavbar
              ? 'calc(100vw - 32px)'
              : 'none',
            justifyContent: isTransparentMobileOverlayNavbar ? 'flex-end' : 'flex-start',
            ml: 'auto',
            mr: {
              xs: isTransparentMobileOverlayNavbar
                ? mobileMapOverlayRightClusterMarginRight
                : mobileNavbarRightClusterMarginRight,
              sm: 1.25,
              lg: 2.25,
            },
            columnGap: { xs: 0.45, sm: 1.25, lg: 1.5 },
            overflow: 'visible',
            pointerEvents: 'auto',
            flexWrap: 'nowrap',
          }}
        >
          {!isSmDown && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mr: { sm: 1.25, lg: 1.5 },
                flexShrink: 0,
              }}
            >
              <Button
                component={Link}
                to="/map"
                variant="text"
                aria-label="Explore"
                sx={{
                  minWidth: 32,
                  width: 32,
                  height: 32,
                  p: 0,
                  borderRadius: 0,
                  color: isMapRoute ? '#ffdb42' : '#fff',
                  backgroundColor: 'transparent',
                  border: 'none',
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <MapRoundedIcon
                  sx={{
                    fontSize: 22,
                  }}
                />
                {renderMapBadge('18px', '0.62rem', {
                  right: -8,
                  top: -5,
                  px: 0,
                })}
              </Button>
            </Box>
          )}

          {/* Lvl + Badge en live */}
          <Box
            sx={{
              flexShrink: 0,
              minWidth: 0,
            }}
          >
            <LiveUserRankBadge
              clientId={clientId}
              isSmDown={isSmDown}
              compact={isSmDown}
              textColor={isSmDown && isMapRoute && !isMobileExplorePlayerOpen ? '#121216' : '#fff'}
              showLevel={shouldShowRankLevel}
              showBadge={shouldShowRankBadge}
            />
          </Box>

          {/* QubiqRepublicButton seulement sur grands écrans */}
          <Box
            sx={{
              display: { xs: 'none', lg: 'block' },
              minWidth: { lg: 280 },
            }}
          >
            <QubiqRepublicButton />
          </Box>

          <Box
            sx={{
              minWidth: 0,
              overflow: 'visible',
              flexShrink: 0,
            }}
          >
            {shouldShowMobileCreateAccountButton ? (
              <Button
                variant="contained"
                size="small"
                onClick={() => openAuth('signup')}
                {...pressable3dHandlers}
                sx={{
                  minHeight: { xs: 32, sm: 36 },
                  px: { xs: 1.15, sm: 1.6 },
                  borderRadius: '5px',
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#ffcf00',
                  color: '#121216',
                  boxShadow: '0px 3px 0 #d38200',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                  transition: 'transform 0.08s ease, box-shadow 0.08s ease, background-color 0.08s ease',
                  '&:hover': {
                    backgroundColor: '#ffed9f',
                    boxShadow: '0px 3px 0 #d38200',
                  },
                  '&:active': {
                    transform: 'translateY(3px)',
                    boxShadow: 'none',
                  },
                }}
              >
                <span
                  className="teko-button-text"
                  style={{
                    transform: 'translateY(0)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    textAlign: 'center',
                  }}
                >
                  Crée ton compte
                </span>
              </Button>
            ) : (
              (isSignedIn || isSmDown) ? (
                <BannerCounter
                  transparentBackground={isTransparentMobileOverlayNavbar}
                  compact={isSmDown}
                  onValueChange={setGuestBannerCounterValue}
                  showWalletLabel={false}
                  textColor={
                    isSmDown && isMapRoute && !isMobileExplorePlayerOpen
                      ? '#121216'
                      : '#fff'
                  }
                />
              ) : null
            )}
          </Box>

          {/* LOGGED OUT —> show auth buttons uniquement desktop/tablette */}
          <SignedOut>
            {!isSmDown && (
              <>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => openAuth('signin')}
                  className="sign-in-button"
                >
                  <span className="teko-button-text">{t("auth.signIn")}</span>
                </Button>

                <Button
                  variant="contained"
                  size="small"
                  onClick={() => openAuth('signup')}
                  className="sign-up-button"
                  {...pressable3dHandlers}
                  sx={{
                    minHeight: 36,
                    px: 1.5,
                    borderRadius: '5px',
                    textTransform: 'none',
                    color: '#121216',
                    backgroundColor: '#ffcf00',
                    boxShadow: '0px 3px 0 #d38200',
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent',
                    transition: 'transform 0.08s ease, box-shadow 0.08s ease, background-color 0.08s ease',
                    '&:hover': {
                      backgroundColor: '#ffed9f',
                      boxShadow: '0px 3px 0 #d38200',
                    },
                    '&:active': {
                      transform: 'translateY(3px)',
                      boxShadow: 'none',
                    },
                  }}
                >
                  <span className="teko-button-text">{t("auth.signUp")}</span>
                </Button>
              </>
            )}
          </SignedOut>

          {/* LOGGED IN —> show UserButton (pas de changement sur desktop) */}
          <SignedIn>
            {!isSmDown && (
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <UserButton
                  userProfileMode="navigation"
                  userProfileUrl="/studio"
                  userProfileProps={{
                    apiKeysProps: { hide: true },
                  }}
                >
                <UserButton.MenuItems>
                  <UserButton.Link
                    href="/notifications"
                    label={
                      hasNotifications
                        ? t("profileMenu.notificationsWithCount", {
                            count: notificationCount > 99 ? '99+' : notificationCount,
                          })
                        : t("profileMenu.notifications")
                    }
                    labelIcon={notificationMenuIcon}
                  />
                  <UserButton.Link
                    href="/messages"
                    label={hasUnreadMessages ? `Messages (${compactMessageLabel})` : 'Messages'}
                    labelIcon={messagesMenuIcon}
                  />
                  <UserButton.Link
                    href={userProfilePath}
                    label={t("profileMenu.myProfile")}
                    labelIcon={<PersonIcon sx={{ fontSize: 'var(--clerk-user-menu-icon-size)' }} />}
                  />
                  <UserButton.Link
                    href="/studio?tab=explore"
                    label={t('profileMenu.myContent', { defaultValue: 'Mon contenu' })}
                    labelIcon={<VideocamIcon sx={{ fontSize: 'var(--clerk-user-menu-icon-size)' }} />}
                  />
                  <UserButton.Link
                    href="/friend-groups"
                    label={t('profileMenu.friendGroups', { defaultValue: "Groupes d'amis" })}
                    labelIcon={<GroupRoundedIcon sx={{ fontSize: 'var(--clerk-user-menu-icon-size)' }} />}
                  />
                  <UserButton.Action
                    label={t("profileMenu.inviteFriendsDesktop", {
                      defaultValue: "Inviter mes amis (+20 jetons qubs)",
                    })}
                    labelIcon={<GroupAddRoundedIcon sx={{ fontSize: 'var(--clerk-user-menu-icon-size)' }} />}
                    onClick={() => openInviteFriendsPrompt()}
                  />
                  <UserButton.Action
                    label={creatorMenuActionLabel}
                    labelIcon={creatorMenuActionIcon}
                    onClick={isCreator ? handleContactCreatorClick : handleCreateOnQubiqClick}
                  />
                  <UserButton.Action
                    label={languageMenuLabel}
                    labelIcon={<TranslateRoundedIcon sx={{ fontSize: 'var(--clerk-user-menu-icon-size)' }} />}
                    onClick={() => setLanguageDialogOpen(true)}
                  />
                  {legalMenuItems}
                </UserButton.MenuItems>
                </UserButton>
                {hasProfileBadge
                  ? renderCountBadge(profileBadgeCount, '18px', '0.65rem', {
                      right: -8,
                      top: -3,
                      px: 0,
                      borderRadius: '50%',
                    })
                  : null}
              </Box>
            )}
          </SignedIn>
        </Stack>
      </Stack>

      {isSmDown && isSearchbarOpen && !isTransparentMobileOverlayNavbar && (
        <>
          <Box sx={{ height: 44 }} />
          <ClickAwayListener onClickAway={() => setIsSearchbarOpen(false)}>
            <Box
              sx={{
                position: 'fixed',
                top: 53,
                left: 0,
                right: 0,
                zIndex: 31,
                bgcolor: '#18181b',
                boxShadow: '0 2px 4px rgba(0,0,0,0.25)',
                px: 4.5,
                pb: 1,
                pt: 0.5,
              }}
            >
              <SearchBar
                mobileMode="panel"
                mobileOpen={isSearchbarOpen}
                onMobileOpenChange={setIsSearchbarOpen}
              />
            </Box>
          </ClickAwayListener>
        </>
      )}

      {/* AUTH POPUP (Clerk) */}
      <Dialog
        open={authOpen}
        onClose={closeAuth}
        fullWidth
        maxWidth="xs"
        fullScreen={isSmDown}
        PaperProps={{
          sx: isSmDown
            ? {
                m: 0,
                width: '100%',
                maxWidth: '100%',
                height: '100dvh',
                maxHeight: '100dvh',
                borderRadius: 0,
                display: 'flex',
                flexDirection: 'column',
              }
            : undefined,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: isSmDown ? '100%' : 'auto',
            height: isSmDown ? '100%' : 'auto',
          }}
        >
          {isSmDown && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                p: 1,
                flexShrink: 0,
              }}
            >
              <Button
                onClick={closeAuth}
                sx={{
                  minWidth: '36px',
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  color: '#0e0e10',
                  fontWeight: 700,
                  fontSize: '13px',
                }}
              >
                ✕
              </Button>
            </Box>
          )}

          <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
            <Tabs
              value={authTab}
              onChange={handleTabChange}
              variant="fullWidth"
              TabIndicatorProps={{ sx: { backgroundColor: "#121216" } }}
              sx={{
                "& .MuiTab-root": {
                  color: "rgba(18,18,22,0.58)",
                  textTransform: "none",
                },
                "& .Mui-selected": {
                  color: "#121216 !important",
                },
              }}
            >
              <Tab label={t("auth.signIn")} value="signin" />
              <Tab label={t("auth.signUp")} value="signup" />
            </Tabs>
          </Box>

          <Box
            sx={{
              p: 2,
              flex: 1,
              minHeight: 0,
              overflowY: isSmDown ? 'auto' : 'visible',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {authTab === "signin" ? (
              <SignIn
                routing="virtual"
                redirectUrl={currentUrl}
                afterSignInUrl={currentUrl}
                appearance={clerkAppearance}
              />
            ) : (
              <SignUp
                routing="virtual"
                redirectUrl={currentUrl}
                afterSignUpUrl={currentUrl}
                appearance={clerkAppearance}
              />
            )}
          </Box>
        </Box>
      </Dialog>

      <Dialog
        open={guestProfileMenuOpen}
        onClose={() => setGuestProfileMenuOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <Box sx={{ p: 2.2, backgroundColor: '#111218' }}>
          <Stack spacing={1.4}>
            <Typography className="teko-title" sx={{ color: '#fff', fontWeight: 900, fontSize: '1.05rem' }}>
              {t("profileMenu.title")}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => openAuth('signin')}
                sx={{
                  textTransform: 'none',
                  borderRadius: '5px',
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.2)',
                }}
              >
                <span className="teko-button-text">{t("auth.signInShort")}</span>
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={() => openAuth('signup')}
                sx={{
                  textTransform: 'none',
                  borderRadius: '5px',
                  color: '#121216',
                  backgroundColor: '#ffdb42',
                  fontWeight: 800,
                  '&:hover': { backgroundColor: '#ffed9f' },
                }}
              >
                <span className="teko-button-text">{t("auth.signUpShort")}</span>
              </Button>
            </Stack>

            <Button
              onClick={() => setLanguageDialogOpen(true)}
              startIcon={<TranslateRoundedIcon sx={{ fontSize: 18 }} />}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                borderRadius: '5px',
                color: '#fff',
                backgroundColor: 'transparent',
                border: 'none',
                px: 1.2,
                py: 1.05,
                '&:hover': {
                  backgroundColor: 'transparent',
                  border: 'none',
                },
              }}
            >
              <span className="teko-button-text">{languageMenuLabel}</span>
            </Button>

            <Box sx={{ pt: 0.4 }}>
              <Typography className="teko-title" sx={{ color: '#aeb3c5', fontSize: '0.92rem', fontWeight: 700, mb: 1 }}>
                {t("profileMenu.legalInfo")}
              </Typography>
              <Stack spacing={1}>
                {LEGAL_LINKS.map((item) => (
                  <Button
                    key={item.key}
                    component={Link}
                    to={item.path}
                    onClick={() => setGuestProfileMenuOpen(false)}
                    startIcon={legalMenuIcons[item.icon]}
                    sx={{
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      borderRadius: '5px',
                      color: '#fff',
                      backgroundColor: 'transparent',
                      border: 'none',
                      px: 1.2,
                      py: 1.05,
                      '&:hover': {
                        backgroundColor: 'transparent',
                        border: 'none',
                      },
                    }}
                  >
                    <span className="teko-button-text">{t(`legalLinks.${item.key}`)}</span>
                  </Button>
                ))}
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Dialog>

      <Dialog
        open={creatorWelcomeOpen}
        onClose={() => setCreatorWelcomeOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <Box sx={{ p: 2.2, backgroundColor: '#111218' }}>
          <Stack spacing={1.6} alignItems="center" textAlign="center">
            <Typography className="teko-title" sx={{ color: '#fff', fontWeight: 900, fontSize: '1.15rem' }}>
              {t("profileMenu.creatorWelcomeTitle")}
            </Typography>
            <Typography sx={{ color: '#d7dae3', fontSize: '0.92rem', lineHeight: 1.55 }}>
              {isMdDown
                ? t("profileMenu.creatorWelcomeMobile")
                : t("profileMenu.creatorWelcomeDesktop")}
            </Typography>
            {isMdDown ? (
              <IconButton
                onClick={() => {
                  setCreatorWelcomeOpen(false);
                  navigate('/studio');
                }}
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: '2px',
                  backgroundColor: '#ffcf00',
                  color: '#121216',
                  boxShadow: '0px 3px 0 #d38200',
                  '&:hover': {
                    backgroundColor: '#ffed9f',
                  },
                }}
              >
                <VideocamIcon sx={{ fontSize: 26 }} />
              </IconButton>
            ) : (
              <Button
                variant="contained"
                onClick={() => {
                  setCreatorWelcomeOpen(false);
                  navigate('/studio');
                }}
                sx={{
                  textTransform: 'none',
                  borderRadius: '2px',
                  backgroundColor: '#ffcf00',
                  boxShadow: '0px 3px 0 #d38200',
                  color: '#121216',
                  fontWeight: 800,
                  '&:hover': {
                    backgroundColor: '#ffed9f',
                  },
                }}
              >
                <Box className="teko-label" component="span">
                  QUBIQ STUDIO
                </Box>
              </Button>
            )}
          </Stack>
        </Box>
      </Dialog>

      <Dialog
        open={creatorContactOpen}
        onClose={() => setCreatorContactOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <Box sx={{ p: 2.2, backgroundColor: '#111218' }}>
          <Stack spacing={1.5} alignItems="center" textAlign="center">
            <Typography className="teko-title" sx={{ color: '#fff', fontWeight: 900, fontSize: '1.1rem' }}>
              {t("profileMenu.contactUs")}
            </Typography>
            <Typography sx={{ color: '#d7dae3', fontSize: '0.92rem', lineHeight: 1.55 }}>
              {t("profileMenu.contactCreatorMessage")}
            </Typography>
          </Stack>
        </Box>
      </Dialog>

      <LanguageDialog
        open={languageDialogOpen}
        onClose={() => setLanguageDialogOpen(false)}
      />
      {clerkPopoverAvatarHost && user
        ? createPortal(
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.35,
                pb: 1.2,
                mb: 1.2,
                borderBottom: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <Box sx={{ position: 'relative', width: 52, height: 52, flexShrink: 0 }}>
                <Avatar
                  src={user.imageUrl || ''}
                  alt={user.username || user.fullName || 'Profile'}
                  onClick={() => desktopProfileImageInputRef.current?.click()}
                  sx={{ width: 52, height: 52, cursor: 'pointer' }}
                />
                <IconButton
                  type="button"
                  aria-label="Modifier la photo de profil"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    desktopProfileImageInputRef.current?.click();
                  }}
                  disabled={desktopAvatarUploading}
                  sx={{
                    position: 'absolute',
                    right: -3,
                    bottom: -3,
                    width: 23,
                    height: 23,
                    borderRadius: '50%',
                    backgroundColor: '#ffcf00',
                    color: '#121216',
                    border: '2px solid #18181b',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.22)',
                    '&:hover': {
                      backgroundColor: '#ffe27a',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#ffcf00',
                      color: '#121216',
                      opacity: 0.7,
                    },
                  }}
                >
                  {desktopAvatarUploading ? (
                    <CircularProgress size={14} sx={{ color: '#121216' }} />
                  ) : (
                    <EditRoundedIcon sx={{ fontSize: 12 }} />
                  )}
                </IconButton>
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: '0.96rem',
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user.username || 'Profil'}
                </Typography>
                <Typography
                  sx={{
                    color: '#fff',
                    fontSize: '0.82rem',
                    lineHeight: 1.35,
                    mt: 0.3,
                  }}
                >
                  Clique sur la photo pour la modifier
                </Typography>
                {desktopAvatarError ? (
                  <Typography sx={{ color: '#ff8f8f', fontSize: '0.75rem', mt: 0.45, lineHeight: 1.35 }}>
                    {desktopAvatarError}
                  </Typography>
                ) : null}
              </Box>
              <Box
                component="input"
                ref={desktopProfileImageInputRef}
                type="file"
                accept="image/*"
                onChange={handleDesktopPopoverAvatarFileChange}
                sx={{ display: 'none' }}
              />
            </Box>,
            clerkPopoverAvatarHost
          )
        : null}

      {/* 👇 BARRE DU BAS MOBILE */}
      {isSmDown && !isMobileExplorePlayerOpen && (
        <>
          <Box
            sx={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              maxWidth: '100vw',
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: '#26262c',
              boxShadow: '0 0 2px rgba(255, 219, 66, 0.2), 0 0 5px rgba(115, 115, 115, 0.1)',
              zIndex: 1200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-around',

              // 👉 Barre stable sur mobile, sans découpe au scroll
              minHeight: '60px',
              paddingTop: '6px',
              paddingBottom: 'calc(env(safe-area-inset-bottom) + 8px)',
              boxSizing: 'border-box',
              overflow: 'visible',
              transform: 'translate3d(0, 0, 0)',
              WebkitTransform: 'translate3d(0, 0, 0)',
              willChange: 'transform',
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              contain: 'layout paint style',
              '@supports (-webkit-touch-callout: none)': {
                paddingBottom: 'calc(env(safe-area-inset-bottom) + 10px)',
              },
            }}
          >
            {/* Home */}
            <Link
              to="/"
              state={{ resetFeed: true }}
              style={{ textDecoration: 'none', color: 'inherit', flex: 1 }}
            >
              <Box sx={{ flex: 1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
                <Box sx={{ height: ICON_SLOT_H, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <AppsRoundedIcon sx={{ color: isHome ? '#ffdb42' : '#fff', fontSize: '26px' }} />
                </Box>
              </Box>
            </Link>

            <Link
              to="/map"
              style={{ textDecoration: 'none', color: 'inherit', flex: 1 }}
            >
              <Box sx={{ flex: 1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
                <Box sx={{ height: ICON_SLOT_H, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <Box sx={{ position: 'relative', width: 32, height: 32, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <MapRoundedIcon
                      sx={{
                        color: isMapRoute ? '#ffdb42' : '#fff',
                        fontSize: '25px',
                      }}
                    />
                    {renderMapBadge('16px', '0.58rem', {
                      right: -1,
                      top: -4,
                    })}
                  </Box>
                </Box>
              </Box>
            </Link>

            <Link
              to="/search/a?default=1"
              aria-label="Recherche"
              style={{ textDecoration: 'none', color: 'inherit', flex: 1 }}
            >
              <Box sx={{ flex: 1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
                <Box sx={{ height: ICON_SLOT_H, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <SearchRoundedIcon sx={{ color: isSearchRoute ? '#ffdb42' : '#fff', fontSize: '26px' }} />
                </Box>
              </Box>
            </Link>

            <Box
              onClick={handleMobileCreateMenuOpen}
              sx={{
                flex: 1,
                display:'flex',
                flexDirection:'column',
                alignItems:'center',
                justifyContent:'center',
                color: isStudioRoute ? '#ffdb42' : '#fff',
                cursor: 'pointer',
              }}
            >
                <Box sx={{ height: ICON_SLOT_H, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <AddBoxRoundedIcon sx={{ color: isStudioRoute ? '#ffdb42' : '#fff', fontSize: '28px' }} />
                </Box>
              </Box>

            {/* Profil */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
              }}
            >
              <SignedOut>
                <PersonIcon
                  onClick={() => setGuestProfileMenuOpen(true)}
                  sx={{ color: profileColorSignedOut, fontSize: '26px' }}
                />
              </SignedOut>

              <SignedIn>
                <Box sx={{ transform: 'scale(1)', mt: -0.21, position: 'relative', display: 'inline-flex' }}>
                  <Box
                    component={Link}
                    to="/account"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textDecoration: 'none',
                    }}
                  >
                    <Avatar
                      src={user?.imageUrl || ''}
                      alt={user?.username || user?.fullName || 'Profile'}
                      sx={{ width: 28, height: 28 }}
                    />
                  </Box>
                  {hasProfileBadge
                    ? renderCountBadge(profileBadgeCount, '18px', '0.65rem', {
                        right: -8,
                        top: -3,
                        px: 0,
                        borderRadius: '50%',
                      })
                    : null}
                </Box>
              </SignedIn>
            </Box>
          </Box>

          <input
            ref={mobileExploreLibraryInputRef}
            type="file"
            accept="video/*"
            hidden
            onChange={handleMobileExploreFileChange}
          />
          <input
            ref={mobileMomentLibraryInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleMobileMomentFileChange}
          />
          <input
            ref={mobileMomentCameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={handleMobileMomentFileChange}
          />
        </>
      )}

      <Dialog
        open={mobileCreateMenuOpen}
        onClose={() => setMobileCreateMenuOpen(false)}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            m: 0,
            mt: 'auto',
            width: '100vw',
            maxWidth: '100vw',
            borderTopLeftRadius: 5,
            borderTopRightRadius: 5,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            backgroundColor: '#18191f',
            color: '#fff',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 900, textAlign: 'center' }}>Créer</DialogTitle>
        <Box sx={{ px: 2, pb: 3 }}>
          <Stack spacing={1.1}>
            <Button
              onClick={() => {
                setMobileCreateMenuOpen(false);
                handleMobileCreateVideoClick();
              }}
              startIcon={<TravelExploreRoundedIcon />}
              sx={{
                justifyContent: 'flex-start',
                borderRadius: '5px',
                px: 1.6,
                py: 1.25,
                textTransform: 'none',
                fontWeight: 800,
                color: '#fff',
                backgroundColor: 'rgba(255,255,255,0.05)',
              }}
            >
              Explore
            </Button>
            <Button
              onClick={handleMobileMomentCreateClick}
              startIcon={<PhotoRoundedIcon />}
              sx={{
                justifyContent: 'flex-start',
                borderRadius: '5px',
                px: 1.6,
                py: 1.25,
                textTransform: 'none',
                fontWeight: 800,
                color: '#fff',
                backgroundColor: 'rgba(255,255,255,0.05)',
              }}
            >
              Moment
            </Button>
          </Stack>
        </Box>
      </Dialog>

      <Dialog
        open={mobileExploreSourceOpen}
        onClose={() => setMobileExploreSourceOpen(false)}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            m: 0,
            mt: 'auto',
            width: '100vw',
            maxWidth: '100vw',
            borderTopLeftRadius: 5,
            borderTopRightRadius: 5,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            backgroundColor: '#18191f',
            color: '#fff',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 900, textAlign: 'center' }}>Nouvel Explore</DialogTitle>
        <Box sx={{ px: 2, pb: 3 }}>
          <Stack spacing={1.1}>
            <Button
              onClick={handleMobileExploreLibraryClick}
              startIcon={<PhotoLibraryRoundedIcon />}
              sx={mobileSourceOptionButtonSx}
            >
              Galerie
            </Button>
            <Button
              onClick={handleMobileExploreCameraClick}
              startIcon={<PhotoCameraRoundedIcon />}
              sx={mobileSourceOptionButtonSx}
            >
              Caméra
            </Button>
          </Stack>
        </Box>
      </Dialog>

      <Dialog
        open={mobileMomentSourceOpen}
        onClose={() => setMobileMomentSourceOpen(false)}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            m: 0,
            mt: 'auto',
            width: '100vw',
            maxWidth: '100vw',
            borderTopLeftRadius: 5,
            borderTopRightRadius: 5,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            backgroundColor: '#18191f',
            color: '#fff',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 900, textAlign: 'center' }}>Nouveau Moment</DialogTitle>
        <Box sx={{ px: 2, pb: 3 }}>
          <Stack spacing={1.1}>
            <Button
              onClick={() => {
                setMobileMomentSourceOpen(false);
                mobileMomentLibraryInputRef.current?.click?.();
              }}
              startIcon={<PhotoLibraryRoundedIcon />}
              sx={mobileSourceOptionButtonSx}
            >
              Photothèque
            </Button>
            <Button
              onClick={handleMobileMomentCameraOpen}
              startIcon={<PhotoCameraRoundedIcon />}
              sx={mobileSourceOptionButtonSx}
            >
              Caméra
            </Button>
          </Stack>
        </Box>
      </Dialog>

      <Dialog
        open={mobileMomentCameraDialogOpen}
        onClose={() => setMobileMomentCameraDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 4,
            backgroundColor: '#18191f',
            color: '#fff',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 900, textAlign: 'center' }}>Prendre une photo</DialogTitle>
        <DialogContent sx={{ px: 2, pb: 2.5 }}>
          <Stack spacing={1.5}>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                aspectRatio: '4 / 3',
                borderRadius: 3,
                overflow: 'hidden',
                backgroundColor: '#0d0e12',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <Box
                component="video"
                ref={mobileMomentCameraVideoRef}
                muted
                playsInline
                autoPlay
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: mobileMomentCameraLoading ? 'none' : 'block',
                  transform: 'scaleX(-1)',
                }}
              />
              {mobileMomentCameraLoading ? (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'grid',
                    placeItems: 'center',
                    color: '#fff',
                    fontWeight: 700,
                  }}
                >
                  Ouverture de la webcam...
                </Box>
              ) : null}
            </Box>

            {mobileMomentCameraError ? (
              <Typography sx={{ color: '#ff8f8f', fontSize: '0.92rem' }}>
                {mobileMomentCameraError}
              </Typography>
            ) : null}

            <Stack direction="row" spacing={1}>
              <Button
                onClick={() => setMobileMomentCameraDialogOpen(false)}
                variant="outlined"
                sx={{
                  flex: 1,
                  borderRadius: '5px',
                  textTransform: 'none',
                  fontWeight: 800,
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.18)',
                }}
              >
                Annuler
              </Button>
              <Button
                onClick={handleDesktopMomentCameraCapture}
                disabled={mobileMomentCameraLoading || Boolean(mobileMomentCameraError)}
                sx={{
                  flex: 1,
                  borderRadius: '5px',
                  textTransform: 'none',
                  fontWeight: 900,
                  color: '#121216',
                  backgroundColor: '#ffcf00',
                  '&:hover': {
                    backgroundColor: '#ffdc52',
                  },
                }}
              >
                Prendre la photo
              </Button>
            </Stack>
            <canvas ref={mobileMomentCameraCanvasRef} hidden />
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Navbar;
